import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import { Alert, Button, FieldWrapper, Icon, Input, Modal, ModalFooter, ModalForm, ModalHeader, ModalTitle, Select, Text } from 'components';
import { HTTPStatusCode } from 'config/const';
import { theme, toRem } from 'design';
import { AuthStepUpVerification } from 'modals';
import { IconContainer } from 'screens/Payments/styles';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getEmployersRequest } from 'store/Employers';
import { Payout, reviewPayoutRequest } from 'store/Payout';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, IAuthMFARequired, IPayout, IReviewPayout, PayoutApprovalStatus } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateAndTime } from 'utils/date';
import { payoutStatusAlertMessage } from 'utils/payout';

const Content = styled.div`
  flex-flow: column nowrap;
  align-items: center;
  background-color: ${theme.color.neutral[50]};
  width: 100%;
  height: auto;
  border-radius: ${toRem(theme.borderRadius.small)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(21)};
`;

const PayoutInfo = styled.div<{ marginBottom?: boolean }>`
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
  gap: ${toRem(8)};

  p {
    word-break: break-word;
    max-width: ${toRem(220)};
    text-align: right;
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(28)};
    `}
`;

const ModalInfo = styled(Text)`
  margin-top: ${toRem(10)};
  font-size: ${toRem(15)};
`;

const Row = styled.div`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Label = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

interface IPayoutReview {
  payout: IPayout;
  onClose: () => void;
  refreshPayouts?: () => void;
}

enum ReviewSteps {
  INITIAL = 'initial',
  STEP_UP = 'stepUp',
  SUCCESS = 'success',
  FAILURE = 'failure',
}

const PayoutReview: React.FC<IPayoutReview> = ({ payout, onClose, refreshPayouts }) => {
  const dispatch = useDispatch();
  const [mfa, setMFA] = useState<IAuthMFARequired>();

  const [currentStep, setCurrentStep] = useState<ReviewSteps>(ReviewSteps.INITIAL);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payout.REVIEW_PAYOUT_REQUEST));
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payout.REVIEW_PAYOUT_REQUEST)) as IApiErrorPayloadWithCode;

  const onMFARequired = (mfa: IAuthMFARequired) => {
    setMFA(mfa);
    setCurrentStep(ReviewSteps.STEP_UP);
    dispatch(clearRequest(Payout.REVIEW_PAYOUT_REQUEST));
  };

  const onSuccess = () => {
    setCurrentStep(ReviewSteps.SUCCESS);
    refreshPayouts?.();
  };

  const onRefresh = () => {
    refreshPayouts?.();
    dispatch(getEmployersRequest()); // reload employers
    onClose();
  };

  const validationSchema = Yup.object().shape({
    status: Yup.string().required('Please select a valid status'),
    desc: Yup.string().trim().required('Required').max(50, 'Note must be at most 50 characters'),
  });

  const { dirty, errors, touched, isValid, values, handleSubmit, getFieldProps, setFieldValue } = useFormik<IReviewPayout>({
    initialValues: { desc: '' },
    onSubmit: () => {
      reviewPayout();
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const reviewPayout = () => {
    dispatch(reviewPayoutRequest(payout.id, values, onMFARequired, onSuccess, () => setCurrentStep(ReviewSteps.FAILURE)));
  };

  const validateField = (field: 'status' | 'desc') => Boolean(touched[field] && errors[field]);

  const getPayoutInfo = (payout: IPayout) => (
    <PayoutInfo data-cy="payout-info" marginBottom={currentStep === ReviewSteps.INITIAL}>
      <Row>
        <Text fontWeight="semibold">{payout.employee.name}</Text>
        <Text fontWeight="semibold">{payout.net ? centsToDollarsDisplay(payout.net) : '--'}</Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Payment ID
        </Label>
        <Text size="small">{payout.id}</Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Date & Time
        </Label>
        <Text size="small">{payout.createdAt ? formatDateAndTime(payout.createdAt) : '--'}</Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Sent By
        </Label>
        <Text size="small">{sentBy?.name}</Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Reason for Review
        </Label>
        <Text size="small">{payoutStatusAlertMessage(payout) || '--'}</Text>
      </Row>
      {currentStep === ReviewSteps.SUCCESS && (
        <Row>
          <Label size="small" fontWeight="medium">
            Reviewer's Note
          </Label>
          <Text size="small">{values.desc || '--'}</Text>
        </Row>
      )}
    </PayoutInfo>
  );

  const sentBy = payout?.statusHistory?.find((status) => status.action === 'create')?.changedBy;

  return (
    <>
      {currentStep === ReviewSteps.INITIAL && (
        <Modal maxWidth={540} isActive onClose={onClose}>
          <ModalHeader>
            <ModalTitle>Review Selected Payment</ModalTitle>
            <ModalInfo>How would you like to resolve this payment?</ModalInfo>
          </ModalHeader>

          <ModalForm onSubmit={handleSubmit}>
            <Content>
              {getPayoutInfo(payout)}

              <FieldWrapper label="Decision *" message={validateField('status') ? errors.status : ''} hasError={validateField('status')}>
                <Select
                  placeholder="Please select"
                  options={[
                    { label: 'Approve', value: PayoutApprovalStatus.REVIEWER_APPROVED },
                    { label: 'Reject', value: PayoutApprovalStatus.REVIEWER_REJECTED },
                  ]}
                  values={values.status ? [values.status] : []}
                  onChange={(selected) => setFieldValue('status', selected?.[0]?.value)}
                  data-cy="status-select"
                />
              </FieldWrapper>

              <FieldWrapper label="Reviewer's Note *" message={validateField('desc') ? errors.desc : ''} hasError={validateField('desc')}>
                <Input id="desc" type="text" placeholder="Enter note here" data-cy="desc-input" {...getFieldProps('desc')} />
              </FieldWrapper>
            </Content>

            <ModalFooter columns={2}>
              <Button label="Cancel" appearance="white" onClick={onClose} />
              <Button label="Confirm" type="submit" isProcessing={isLoading} disabled={!dirty || !isValid} data-cy="confirm-button" />
            </ModalFooter>
          </ModalForm>
        </Modal>
      )}
      {currentStep === ReviewSteps.STEP_UP && (
        <AuthStepUpVerification
          authId={mfa.authId}
          phoneLast4={mfa.phoneLast4}
          subtitle="In order to act on these payments, we must authenticate your account."
          backLabel="Back to Review Payments"
          onSuccess={reviewPayout}
          onClose={onClose}
        />
      )}
      {currentStep === ReviewSteps.SUCCESS && (
        <Modal maxWidth={540} isActive onClose={onClose} dataCy="review-success-modal">
          <ModalHeader>
            <IconContainer>
              <Icon icon="CheckmarkThick" fill={theme.color.white} width={45} height={45} />
            </IconContainer>
            <ModalTitle>Success!</ModalTitle>
            <ModalInfo>
              The payment has been <b>{values.status === PayoutApprovalStatus.REVIEWER_APPROVED ? 'approved' : 'rejected'}</b>.
            </ModalInfo>
          </ModalHeader>

          <Content>{getPayoutInfo(payout)}</Content>

          <ModalFooter columns={1}>
            <Button label="Back to Review Payments" appearance="white" onClick={onClose} data-cy="back-button" />
          </ModalFooter>
        </Modal>
      )}
      {currentStep === ReviewSteps.FAILURE && (
        <Alert
          isActive
          title="An Error Has Occurred"
          message={apiErrors?.statusCode === HTTPStatusCode.CONFLICT ? apiErrors.errorMessages[0] : 'Please refresh to see payments in review'}
          dataCy="review-failure-modal"
          hideLeftAction
          rightActionLabel="Refresh Review Page"
          rightActionAppearance="white"
          onRightAction={onRefresh}
          maxWidth={420}
        />
      )}
    </>
  );
};

export default PayoutReview;
